'use client'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  AnimatePresence,
  motion,
} from 'framer-motion'

import {
  Check,
  CreditCard,
  Infinity as InfinityIcon,
  Loader2,
  Lock,
  RefreshCw,
  ShoppingCart,
} from 'lucide-react'

import { useRouter } from 'next/navigation'

import { Button } from '@/components/ui/button'

import { useCurrentUser } from '@/hooks/useCurrentUser'

import { CheckoutFormBlock } from '@/components/funnel/blocks/CheckoutFormBlock'

import { buildPurchaseContext } from '@/lib/checkout/buildPurchaseContext'

type GateStep =
  | 'preview'
  | 'signup'
  | 'checkout'

type PurchaseMode =
  | 'recurring'
  | 'one_time'

type CommunityRecurringItem = {
  recurring_id: number

  name: string

  price: number

  frequency:
    | 'day'
    | 'week'
    | 'semi_month'
    | 'month'
    | 'year'
    | string

  duration: number
  cycle: number

  trial_status?: boolean
  trial_price?: number

  trial_frequency?:
    | 'day'
    | 'week'
    | 'semi_month'
    | 'month'
    | 'year'
    | string

  trial_duration?: number
  trial_cycle?: number

  status?: boolean

  product_id?:
    number | null

  product_name?:
    string | null

  currency?:
    string | null
}

function formatMoney(
  value: number,
  currency = 'USD'
) {
  return new Intl.NumberFormat(
    'es-MX',
    {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  )
}

function frequencyLabel(
  value: string
) {
  switch (value) {
    case 'day':
      return 'día'

    case 'week':
      return 'semana'

    case 'semi_month':
      return 'quincena'

    case 'month':
      return 'mes'

    case 'year':
      return 'año'

    default:
      return value
  }
}

function cleanIds(
  values: number[]
): number[] {
  return Array.from(
    new Set(
      (
        Array.isArray(values)
          ? values
          : []
      )
        .map(
          (value) =>
            Number(value)
        )
        .filter(
          (value) =>
            Number.isFinite(
              value
            ) &&
            value > 0
        )
    )
  )
}

export function CommunityPaidAccessModal({
  sellerId,
  productId,
  recurringIds,
  title = 'Acceso premium',
  onSuccess,
}: {
  sellerId: number

  productId:
    number | null

  recurringIds:
    number[]

  title?: string

  onSuccess?:
    () =>
      | void
      | Promise<void>
}) {
  const {
    loggedIn,
    refreshUser,
  } =
    useCurrentUser()

  const router =
    useRouter()

  const [step, setStep] =
    useState<GateStep>(
      'preview'
    )

  const [
    purchaseMode,
    setPurchaseMode,
  ] =
    useState<PurchaseMode | null>(
      null
    )

  const [
    loadingPlans,
    setLoadingPlans,
  ] =
    useState(false)

  const [
    loadingBaseProduct,
    setLoadingBaseProduct,
  ] =
    useState(false)

  const [
    loadingCheckoutProduct,
    setLoadingCheckoutProduct,
  ] =
    useState(false)

  const [
    plansError,
    setPlansError,
  ] =
    useState<string | null>(
      null
    )

  const [
    productError,
    setProductError,
  ] =
    useState<string | null>(
      null
    )

  const [
    checkoutError,
    setCheckoutError,
  ] =
    useState<string | null>(
      null
    )

  const [
    plans,
    setPlans,
  ] =
    useState<
      CommunityRecurringItem[]
    >([])

  const [
    selectedRecurringId,
    setSelectedRecurringId,
  ] =
    useState<number | null>(
      null
    )

  /**
   * Producto interno de comunidad.
   *
   * COMMUNITY_ACCESS_{sellerId}
   *
   * Aquí vive el precio de pago único
   * y normalmente también los recurrings.
   */
  const [
    baseProduct,
    setBaseProduct,
  ] =
    useState<any | null>(
      null
    )

  /**
   * Producto que se manda finalmente
   * al checkout.
   *
   * Para pago único será baseProduct.
   *
   * Para recurring usamos product_id
   * del plan y hacemos fallback al
   * producto interno.
   */
  const [
    checkoutProduct,
    setCheckoutProduct,
  ] =
    useState<any | null>(
      null
    )

  const cleanedRecurringIds =
    useMemo(
      () =>
        cleanIds(
          recurringIds
        ),
      [
        recurringIds,
      ]
    )

  const recurringIdsKey =
    cleanedRecurringIds.join(
      ','
    )

  /**
   * CARGAR PRODUCTO INTERNO
   */
  useEffect(() => {
    let mounted = true

    async function loadBaseProduct() {
      const id =
        Number(
          productId || 0
        )

      if (!id) {
        if (mounted) {
          setBaseProduct(
            null
          )
        }

        return
      }

      try {
        setLoadingBaseProduct(
          true
        )

        setProductError(
          null
        )

        const res =
          await fetch(
            `/api/product/${id}`,
            {
              method:
                'GET',

              cache:
                'no-store',
            }
          )

        const json =
          await res
            .json()
            .catch(
              () => null
            )

        if (!res.ok) {
          throw new Error(
            json?.error ||
              'No se pudo cargar el producto de acceso'
          )
        }

        if (!mounted) {
          return
        }

        setBaseProduct(
          json
        )
      } catch (e: any) {
        if (!mounted) {
          return
        }

        setBaseProduct(
          null
        )

        setProductError(
          e?.message ||
            'No se pudo cargar el producto de acceso'
        )
      } finally {
        if (mounted) {
          setLoadingBaseProduct(
            false
          )
        }
      }
    }

    loadBaseProduct()

    return () => {
      mounted = false
    }
  }, [
    productId,
  ])

  /**
   * CARGAR SUSCRIPCIONES
   */
  useEffect(() => {
    let mounted = true

    async function loadPlans() {
      if (
        !sellerId ||
        cleanedRecurringIds.length ===
          0
      ) {
        if (mounted) {
          setPlans([])
          setSelectedRecurringId(
            null
          )
        }

        return
      }

      try {
        setLoadingPlans(
          true
        )

        setPlansError(
          null
        )

        const qs =
          new URLSearchParams()

        qs.set(
          'sellerId',
          String(sellerId)
        )

        qs.set(
          'ids',
          recurringIdsKey
        )

        const res =
          await fetch(
            `/api/community/recurrings?${qs.toString()}`,
            {
              method:
                'GET',

              cache:
                'no-store',
            }
          )

        const json =
          await res
            .json()
            .catch(
              () => null
            )

        if (!res.ok) {
          throw new Error(
            json?.error ||
              'No se pudieron cargar los planes'
          )
        }

        if (!mounted) {
          return
        }

        const nextPlans =
          Array.isArray(
            json?.plans
          )
            ? json.plans
                .filter(
                  (
                    plan: any
                  ) =>
                    Number(
                      plan?.recurring_id ||
                        0
                    ) > 0
                )
            : []

        setPlans(
          nextPlans
        )

        setSelectedRecurringId(
          (
            previous
          ) => {
            const stillExists =
              nextPlans.some(
                (
                  plan: any
                ) =>
                  Number(
                    plan.recurring_id
                  ) ===
                  Number(
                    previous
                  )
              )

            if (
              stillExists
            ) {
              return previous
            }

            return nextPlans.length >
              0
              ? Number(
                  nextPlans[0]
                    .recurring_id
                )
              : null
          }
        )
      } catch (e: any) {
        if (!mounted) {
          return
        }

        setPlans([])

        setSelectedRecurringId(
          null
        )

        setPlansError(
          e?.message ||
            'No se pudieron cargar los planes'
        )
      } finally {
        if (mounted) {
          setLoadingPlans(
            false
          )
        }
      }
    }

    loadPlans()

    return () => {
      mounted = false
    }
  }, [
    sellerId,
    recurringIdsKey,
  ])

  const selectedPlan =
    useMemo(() => {
      if (
        !selectedRecurringId
      ) {
        return null
      }

      return (
        plans.find(
          (plan) =>
            Number(
              plan.recurring_id
            ) ===
            Number(
              selectedRecurringId
            )
        ) || null
      )
    }, [
      plans,
      selectedRecurringId,
    ])

  const activeRecurring =
    useMemo(() => {
      if (!selectedPlan) {
        return null
      }

      return {
        recurring_id:
          Number(
            selectedPlan.recurring_id
          ),

        price:
          Number(
            selectedPlan.price ||
              0
          ),

        frequency:
          String(
            selectedPlan.frequency ||
              'month'
          ),

        cycle:
          Number(
            selectedPlan.cycle ||
              1
          ),

        duration:
          Number(
            selectedPlan.duration ||
              0
          ),

        trial_status:
          !!selectedPlan.trial_status,

        trial_price:
          Number(
            selectedPlan.trial_price ||
              0
          ),

        trial_frequency:
          String(
            selectedPlan.trial_frequency ||
              'month'
          ),

        trial_cycle:
          Number(
            selectedPlan.trial_cycle ||
              0
          ),

        trial_duration:
          Number(
            selectedPlan.trial_duration ||
              0
          ),
      }
    }, [
      selectedPlan,
    ])

  /**
   * PRECIO PAGO ÚNICO
   */
  const oneTimeSpecial =
    Number(
      baseProduct?.special ||
        0
    )

  const oneTimeRegular =
    Number(
      baseProduct?.price ||
        0
    )

  const oneTimeSpecialUSD =
    Number(
      baseProduct?.specialUSD ||
        0
    )

  const oneTimeRegularUSD =
    Number(
      baseProduct?.priceUSD ||
        0
    )

  const hasOneTimeSpecial =
    oneTimeSpecial > 0 &&
    oneTimeSpecial <
      oneTimeRegular

  const hasOneTimeSpecialUSD =
    oneTimeSpecialUSD >
      0 &&
    oneTimeSpecialUSD <
      oneTimeRegularUSD

  const oneTimePrice =
    hasOneTimeSpecial
      ? oneTimeSpecial
      : oneTimeRegular

  const oneTimePriceUSD =
    hasOneTimeSpecialUSD
      ? oneTimeSpecialUSD
      : oneTimeRegularUSD

  /**
   * OPCIONES DISPONIBLES
   */
  const hasOneTime =
    !!productId &&
    !!baseProduct &&
    oneTimePrice > 0

  const hasRecurring =
    plans.length > 0

  /**
   * Elegir automáticamente una modalidad
   * inicial cuando terminan de cargar las
   * opciones.
   *
   * Priorizamos recurring porque normalmente
   * es la oferta principal / menor barrera.
   */
  useEffect(() => {
    if (
      loadingPlans ||
      loadingBaseProduct
    ) {
      return
    }

    setPurchaseMode(
      (
        current
      ) => {
        if (
          current ===
            'recurring' &&
          hasRecurring
        ) {
          return current
        }

        if (
          current ===
            'one_time' &&
          hasOneTime
        ) {
          return current
        }

        if (hasRecurring) {
          return 'recurring'
        }

        if (hasOneTime) {
          return 'one_time'
        }

        return null
      }
    )
  }, [
    loadingPlans,
    loadingBaseProduct,
    hasRecurring,
    hasOneTime,
  ])

  /**
   * Cada vez que cambia la modalidad o
   * recurring seleccionado, resolvemos
   * qué producto se usará en checkout.
   */
  useEffect(() => {
    let mounted = true

    async function loadCheckoutProduct() {
      if (!purchaseMode) {
        if (mounted) {
          setCheckoutProduct(
            null
          )
        }

        return
      }

      const resolvedProductId =
        purchaseMode ===
        'one_time'
          ? Number(
              productId || 0
            )
          : Number(
              selectedPlan?.product_id ||
                productId ||
                0
            )

      if (!resolvedProductId) {
        if (mounted) {
          setCheckoutProduct(
            null
          )

          setCheckoutError(
            'No se encontró el producto de checkout para esta opción.'
          )
        }

        return
      }

      /**
       * Si ya tenemos cargado el mismo
       * producto interno, reutilizamos
       * baseProduct.
       */
      if (
        Number(
          productId || 0
        ) ===
          resolvedProductId &&
        baseProduct
      ) {
        if (mounted) {
          setCheckoutProduct({
            ...baseProduct,

            selectedRecurringId:
              purchaseMode ===
              'recurring'
                ? selectedPlan?.recurring_id ??
                  null
                : null,
          })

          setCheckoutError(
            null
          )
        }

        return
      }

      try {
        setLoadingCheckoutProduct(
          true
        )

        setCheckoutError(
          null
        )

        const res =
          await fetch(
            `/api/product/${resolvedProductId}`,
            {
              method:
                'GET',

              cache:
                'no-store',
            }
          )

        const json =
          await res
            .json()
            .catch(
              () => null
            )

        if (!res.ok) {
          throw new Error(
            json?.error ||
              'No se pudo cargar el producto del plan'
          )
        }

        if (!mounted) {
          return
        }

        setCheckoutProduct({
          ...json,

          selectedRecurringId:
            purchaseMode ===
            'recurring'
              ? selectedPlan?.recurring_id ??
                null
              : null,
        })
      } catch (e: any) {
        if (!mounted) {
          return
        }

        setCheckoutProduct(
          null
        )

        setCheckoutError(
          e?.message ||
            'No se pudo cargar el producto del plan'
        )
      } finally {
        if (mounted) {
          setLoadingCheckoutProduct(
            false
          )
        }
      }
    }

    loadCheckoutProduct()

    return () => {
      mounted = false
    }
  }, [
    purchaseMode,
    productId,
    baseProduct,
    selectedPlan?.product_id,
    selectedPlan?.recurring_id,
  ])

  const recurringPrice =
    Number(
      activeRecurring?.price ||
        0
    )

  const price =
    purchaseMode ===
    'one_time'
      ? oneTimePrice
      : recurringPrice

  const priceUSD =
    purchaseMode ===
    'one_time'
      ? oneTimePriceUSD
      : recurringPrice

  const selectedCurrency =
    purchaseMode ===
    'recurring'
      ? selectedPlan?.currency ||
        checkoutProduct?.currency ||
        'USD'
      : baseProduct?.currency ||
        checkoutProduct?.currency ||
        'USD'

  const initialLoading =
    loadingPlans ||
    loadingBaseProduct

  const noPurchaseOptions =
    !initialLoading &&
    !hasOneTime &&
    !hasRecurring

  async function handleUnlock() {
    if (!purchaseMode) {
      return
    }

    if (
      purchaseMode ===
        'recurring' &&
      !selectedPlan
    ) {
      return
    }

    if (
      purchaseMode ===
        'one_time' &&
      !hasOneTime
    ) {
      return
    }

    if (!loggedIn) {
      setStep(
        'signup'
      )

      return
    }

    setStep(
      'checkout'
    )
  }

  if (initialLoading) {
    return (
      <div className="fixed inset-0 z-[9999]">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-md" />

        <div className="relative z-10 flex h-full w-full items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-border bg-background/95 p-6 text-foreground shadow-2xl">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />

              Cargando opciones de
              acceso...
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" />

      <div className="relative z-10 flex h-full w-full items-center justify-center overflow-y-auto p-4 sm:p-6">
        <div className="my-auto w-full max-w-2xl rounded-2xl border border-border bg-background/95 p-5 text-foreground shadow-2xl sm:p-6">
          <AnimatePresence mode="wait">
            {/* PREVIEW */}

            {step ===
            'preview' ? (
              <motion.div
                key="preview"
                initial={{
                  opacity: 0,
                  y: 18,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -18,
                }}
                className="space-y-6"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Lock className="h-6 w-6 text-foreground" />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold">
                      {title}
                    </h2>

                    <p className="mt-1 text-sm text-muted-foreground">
                      Elige cómo quieres
                      acceder a esta
                      comunidad.
                    </p>
                  </div>
                </div>

                {noPurchaseOptions ? (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    Esta comunidad todavía
                    no tiene una opción de
                    compra configurada.
                  </div>
                ) : null}

                {plansError &&
                !hasRecurring ? (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {plansError}
                  </div>
                ) : null}

                {productError &&
                !hasOneTime &&
                !hasRecurring ? (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {productError}
                  </div>
                ) : null}

                <div className="space-y-3">
                  {/* RECURRINGS */}

                  {hasRecurring
                    ? plans.map(
                        (
                          plan
                        ) => {
                          const planSelected =
                            purchaseMode ===
                              'recurring' &&
                            Number(
                              selectedRecurringId
                            ) ===
                              Number(
                                plan.recurring_id
                              )

                          return (
                            <button
                              key={
                                plan.recurring_id
                              }
                              type="button"
                              onClick={() => {
                                setPurchaseMode(
                                  'recurring'
                                )

                                setSelectedRecurringId(
                                  Number(
                                    plan.recurring_id
                                  )
                                )
                              }}
                              className={`flex w-full items-start justify-between gap-4 rounded-xl border p-4 text-left transition ${
                                planSelected
                                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                                  : 'border-border hover:bg-muted/30'
                              }`}
                            >
                              <div className="flex min-w-0 items-start gap-3">
                                <div
                                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                                    planSelected
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'border-border'
                                  }`}
                                >
                                  {planSelected ? (
                                    <Check className="h-4 w-4" />
                                  ) : null}
                                </div>

                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <RefreshCw className="h-4 w-4 shrink-0 text-muted-foreground" />

                                    <div className="font-medium">
                                      {
                                        plan.name
                                      }
                                    </div>
                                  </div>

                                  <div className="mt-1 text-sm text-muted-foreground">
                                    {formatMoney(
                                      plan.price,
                                      plan.currency ||
                                        'USD'
                                    )}{' '}
                                    cada{' '}
                                    {
                                      plan.cycle
                                    }{' '}
                                    {frequencyLabel(
                                      plan.frequency
                                    )}

                                    {plan.duration >
                                    0
                                      ? ` · duración ${plan.duration}`
                                      : ''}
                                  </div>

                                  {plan.trial_status ? (
                                    <div className="mt-1 text-xs text-muted-foreground">
                                      Prueba:{' '}
                                      {formatMoney(
                                        Number(
                                          plan.trial_price ||
                                            0
                                        ),
                                        plan.currency ||
                                          'USD'
                                      )}{' '}
                                      cada{' '}
                                      {plan.trial_cycle ||
                                        0}{' '}
                                      {frequencyLabel(
                                        plan.trial_frequency ||
                                          'month'
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              </div>

                              <div className="shrink-0 text-right">
                                <div className="text-lg font-semibold">
                                  {formatMoney(
                                    plan.price,
                                    plan.currency ||
                                      'USD'
                                  )}
                                </div>

                                <div className="text-xs text-muted-foreground">
                                  {plan.cycle ===
                                    1
                                    ? `/${frequencyLabel(
                                        plan.frequency
                                      )}`
                                    : ''}
                                </div>
                              </div>
                            </button>
                          )
                        }
                      )
                    : null}

                  {/* PAGO ÚNICO */}

                  {hasOneTime ? (
                    <button
                      type="button"
                      onClick={() =>
                        setPurchaseMode(
                          'one_time'
                        )
                      }
                      className={`flex w-full items-start justify-between gap-4 rounded-xl border p-4 text-left transition ${
                        purchaseMode ===
                        'one_time'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-border hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <div
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                            purchaseMode ===
                            'one_time'
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border'
                          }`}
                        >
                          {purchaseMode ===
                          'one_time' ? (
                            <Check className="h-4 w-4" />
                          ) : null}
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <InfinityIcon className="h-4 w-4 text-muted-foreground" />

                            <div className="font-medium">
                              Acceso de por
                              vida
                            </div>
                          </div>

                          <div className="mt-1 text-sm text-muted-foreground">
                            Un solo pago.
                            Sin renovaciones.
                          </div>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <div className="text-lg font-semibold">
                          {formatMoney(
                            oneTimePrice,
                            baseProduct?.currency ||
                              'USD'
                          )}
                        </div>

                        <div className="text-xs text-muted-foreground">
                          pago único
                        </div>
                      </div>
                    </button>
                  ) : null}
                </div>

                <Button
                  className="w-full text-base"
                  onClick={
                    handleUnlock
                  }
                  disabled={
                    !purchaseMode ||
                    noPurchaseOptions ||
                    loadingCheckoutProduct ||
                    (
                      purchaseMode ===
                        'recurring' &&
                      !selectedPlan
                    )
                  }
                >
                  {loadingCheckoutProduct ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <ShoppingCart className="mr-2 h-5 w-5" />
                  )}

                  {purchaseMode ===
                  'one_time'
                    ? `Obtener acceso por ${formatMoney(
                        oneTimePrice,
                        baseProduct?.currency ||
                          'USD'
                      )}`
                    : selectedPlan
                      ? `Continuar por ${formatMoney(
                          selectedPlan.price,
                          selectedPlan.currency ||
                            'USD'
                        )}`
                      : 'Continuar'}
                </Button>
              </motion.div>
            ) : null}

            {/* LOGIN */}

            {step ===
            'signup' ? (
              <motion.div
                key="signup"
                initial={{
                  opacity: 0,
                  x: 36,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                exit={{
                  opacity: 0,
                  x: -36,
                }}
                className="space-y-5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
                    <CreditCard className="h-6 w-6" />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold">
                      Inicia sesión para
                      continuar
                    </h2>

                    <p className="mt-1 text-sm text-muted-foreground">
                      Necesitas iniciar
                      sesión o registrarte
                      antes de completar tu
                      compra.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() =>
                      setStep(
                        'preview'
                      )
                    }
                  >
                    Volver
                  </Button>

                  <Button
                    className="flex-1"
                    onClick={() => {
                      const next =
                        encodeURIComponent(
                          window.location.href
                        )

                      window.location.href =
                        `/login?redirect=${next}`
                    }}
                  >
                    Iniciar sesión
                  </Button>
                </div>
              </motion.div>
            ) : null}

            {/* CHECKOUT */}

            {step ===
              'checkout' &&
            purchaseMode ? (
              <motion.div
                key="checkout"
                initial={{
                  opacity: 0,
                  x: 36,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                exit={{
                  opacity: 0,
                  x: -36,
                }}
                className="space-y-5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
                    <CreditCard className="h-6 w-6" />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold">
                      {purchaseMode ===
                      'one_time'
                        ? 'Finaliza tu compra'
                        : 'Finaliza tu suscripción'}
                    </h2>

                    <p className="mt-1 text-sm text-muted-foreground">
                      {purchaseMode ===
                      'one_time'
                        ? 'Completa este pago único para obtener acceso permanente a la comunidad.'
                        : 'Completa tu suscripción para desbloquear el acceso a la comunidad.'}
                    </p>
                  </div>
                </div>

                {checkoutError ? (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    {checkoutError}
                  </div>
                ) : null}

                {loadingCheckoutProduct ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />

                    Cargando checkout...
                  </div>
                ) : !checkoutProduct ? (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                    No se pudo cargar el
                    producto de checkout
                    para esta opción.
                  </div>
                ) : (
                  <CheckoutFormBlock
                    data={{
                      ...buildPurchaseContext({
                        product: {
                          ...checkoutProduct,

                          selectedRecurringId:
                            purchaseMode ===
                              'recurring'
                              ? activeRecurring?.recurring_id ??
                                null
                              : null,
                        },

                        activeRecurring:
                          purchaseMode ===
                            'recurring'
                            ? activeRecurring
                            : null,

                        selectedRecurringId:
                          purchaseMode ===
                            'recurring'
                            ? activeRecurring?.recurring_id ??
                              null
                            : null,

                        price,

                        priceUSD,

                        paymentMethods:
                          checkoutProduct?.paymentMethods ||
                          [],

                        recurringPlan:
                          purchaseMode ===
                            'recurring' &&
                          activeRecurring
                            ? {
                                recurring_id:
                                  Number(
                                    activeRecurring.recurring_id
                                  ),

                                price:
                                  Number(
                                    activeRecurring.price ||
                                      0
                                  ),

                                frequency:
                                  String(
                                    activeRecurring.frequency ||
                                      'month'
                                  ),

                                cycle:
                                  Number(
                                    activeRecurring.cycle ||
                                      1
                                  ),

                                duration:
                                  Number(
                                    activeRecurring.duration ||
                                      0
                                  ),

                                trial: {
                                  enabled:
                                    !!activeRecurring.trial_status,

                                  price:
                                    Number(
                                      activeRecurring.trial_price ||
                                        0
                                    ),

                                  frequency:
                                    String(
                                      activeRecurring.trial_frequency ||
                                        'month'
                                    ),

                                  cycle:
                                    Number(
                                      activeRecurring.trial_cycle ||
                                        0
                                    ),

                                  duration:
                                    Number(
                                      activeRecurring.trial_duration ||
                                        0
                                    ),
                                },
                              }
                            : null,
                      }),

                      recurringPlan:
                        purchaseMode ===
                          'recurring' &&
                        activeRecurring
                          ? {
                              recurring_id:
                                Number(
                                  activeRecurring.recurring_id
                                ),

                              price:
                                Number(
                                  activeRecurring.price ||
                                    0
                                ),

                              frequency:
                                String(
                                  activeRecurring.frequency ||
                                    'month'
                                ),

                              cycle:
                                Number(
                                  activeRecurring.cycle ||
                                    1
                                ),

                              duration:
                                Number(
                                  activeRecurring.duration ||
                                    0
                                ),

                              trial: {
                                enabled:
                                  !!activeRecurring.trial_status,

                                price:
                                  Number(
                                    activeRecurring.trial_price ||
                                      0
                                  ),

                                frequency:
                                  String(
                                    activeRecurring.trial_frequency ||
                                      'month'
                                  ),

                                cycle:
                                  Number(
                                    activeRecurring.trial_cycle ||
                                      0
                                  ),

                                duration:
                                  Number(
                                    activeRecurring.trial_duration ||
                                      0
                                  ),
                              },
                            }
                          : null,

                      amount:
                        Number(
                          price ||
                            0
                        ),

                      amountUSD:
                        Number(
                          priceUSD ||
                            0
                        ),

                      platform: {
                        enabled:
                          true,

                        kind:
                          'community_access',

                        seller_id:
                          sellerId,

                        /**
                         * Conservamos este
                         * campo porque el
                         * backend de checkout
                         * existente puede
                         * depender de él.
                         *
                         * Ahora representa
                         * la opción que el
                         * usuario eligió,
                         * NO la configuración
                         * global de comunidad.
                         */
                        access_mode:
                          purchaseMode,
                      },

                      onSuccess:
                        async () => {
                          await refreshUser?.()

                          let unlocked =
                            false

                          let attempts =
                            0

                          const maxAttempts =
                            6

                          while (
                            !unlocked &&
                            attempts <
                              maxAttempts
                          ) {
                            attempts +=
                              1

                            try {
                              const res =
                                await fetch(
                                  `/api/community/access-status?sellerId=${sellerId}`,
                                  {
                                    method:
                                      'GET',

                                    cache:
                                      'no-store',
                                  }
                                )

                              const json =
                                await res
                                  .json()
                                  .catch(
                                    () =>
                                      null
                                  )

                              if (
                                res.ok &&
                                (
                                  json?.access
                                    ?.hasActivePaidAccess ||
                                  json?.access
                                    ?.status ===
                                    'approved' ||
                                  json?.access
                                    ?.gateType !==
                                    'paid'
                                )
                              ) {
                                unlocked =
                                  true

                                break
                              }
                            } catch {
                              // Reintenta.
                            }

                            await new Promise(
                              (
                                resolve
                              ) =>
                                setTimeout(
                                  resolve,
                                  1000
                                )
                            )
                          }

                          await onSuccess?.()

                          router.refresh()
                        },
                    }}
                    isEditing={
                      false
                    }
                  />
                )}

                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    setCheckoutError(
                      null
                    )

                    setStep(
                      'preview'
                    )
                  }}
                >
                  Volver a opciones
                </Button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}